import React from 'react';
import {
  AbsoluteFill,
  Img,
  OffthreadVideo,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';

type ThemeLike = {
  palette?: Record<string, string | undefined>;
  typography?: Record<string, string | undefined>;
};

type VideoKitTheme = Required<ThemeLike>;

const DEFAULT_THEME: VideoKitTheme = {
  palette: {
    primary: '#10131f',
    secondary: '#20263a',
    accent: '#8b5cf6',
    accent2: '#22d3ee',
    text: '#f8fafc',
    muted: '#a5b4fc',
    surface: 'rgba(15, 23, 42, 0.72)',
  },
  typography: {
    display: 'Georgia, serif',
    body: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
};

function themeOf(theme?: ThemeLike): VideoKitTheme {
  return {
    palette: { ...DEFAULT_THEME.palette, ...(theme?.palette ?? {}) },
    typography: { ...DEFAULT_THEME.typography, ...(theme?.typography ?? {}) },
  };
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function progress(frame: number, startFrame: number, durationFrames: number): number {
  if (durationFrames <= 0) return frame >= startFrame ? 1 : 0;
  return clamp01((frame - startFrame) / durationFrames);
}

function fade(frame: number, startFrame: number, durationFrames: number): number {
  return interpolate(frame, [startFrame, startFrame + durationFrames], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
}

function videoLike(src: string): boolean {
  return /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(src);
}

type SceneShellProps = {
  theme?: ThemeLike;
  children: React.ReactNode;
  variant?: 'aurora' | 'studio' | 'paper' | 'midnight';
  vignette?: boolean;
  style?: React.CSSProperties;
};

function SceneShell({
  theme,
  children,
  variant = 'aurora',
  vignette = true,
  style,
}: SceneShellProps) {
  const frame = useCurrentFrame();
  const t = progress(frame, 0, 90);
  const resolved = themeOf(theme);
  const palette = resolved.palette;
  const bg =
    variant === 'paper'
      ? `linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)`
      : variant === 'studio'
        ? `radial-gradient(circle at 20% 15%, ${palette.accent}44, transparent 34%), radial-gradient(circle at 85% 30%, ${palette.accent2}36, transparent 32%), linear-gradient(145deg, ${palette.primary}, ${palette.secondary})`
        : variant === 'midnight'
          ? `linear-gradient(145deg, #030712 0%, ${palette.primary} 48%, #111827 100%)`
          : `radial-gradient(circle at ${25 + t * 20}% 18%, ${palette.accent}55, transparent 34%), radial-gradient(circle at ${80 - t * 10}% 75%, ${palette.accent2}40, transparent 38%), linear-gradient(145deg, ${palette.primary}, ${palette.secondary})`;

  return (
    <AbsoluteFill
      style={{
        background: bg,
        color: palette.text,
        fontFamily: resolved.typography.body,
        overflow: 'hidden',
        ...style,
      }}
    >
      {children}
      {vignette ? (
        <AbsoluteFill
          style={{
            pointerEvents: 'none',
            background:
              'radial-gradient(circle at 50% 45%, transparent 0%, transparent 58%, rgba(0,0,0,0.38) 100%)',
          }}
        />
      ) : null}
    </AbsoluteFill>
  );
}

type KineticTitleProps = {
  title: string;
  subtitle?: string;
  kicker?: string;
  theme?: ThemeLike;
  align?: 'left' | 'center';
  maxWidth?: number | string;
};

function KineticTitle({
  title,
  subtitle,
  kicker,
  theme,
  align = 'center',
  maxWidth = 980,
}: KineticTitleProps) {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const resolved = themeOf(theme);
  const enter = spring({ frame, fps, config: { damping: 18, stiffness: 85 } });
  const opacity = fade(frame, 0, 24);
  const textAlign = align === 'left' ? 'left' : 'center';

  return (
    <div
      style={{
        maxWidth,
        margin: align === 'left' ? '0' : '0 auto',
        opacity,
        transform: `translateY(${(1 - enter) * 32}px) scale(${0.96 + enter * 0.04})`,
      }}
    >
      {kicker ? (
        <div
          style={{
            color: resolved.palette.accent2,
            fontSize: 24,
            fontWeight: 800,
            letterSpacing: 0,
            marginBottom: 18,
            textAlign,
            textTransform: 'uppercase',
          }}
        >
          {kicker}
        </div>
      ) : null}
      <h1
        style={{
          color: resolved.palette.text,
          fontFamily: resolved.typography.display,
          fontSize: 76,
          lineHeight: 0.96,
          margin: 0,
          textAlign,
        }}
      >
        {title}
      </h1>
      {subtitle ? (
        <p
          style={{
            color: resolved.palette.muted,
            fontSize: 30,
            lineHeight: 1.32,
            margin: '24px 0 0',
            textAlign,
          }}
        >
          {subtitle}
        </p>
      ) : null}
    </div>
  );
}

type MediaFrameProps = {
  src: string;
  caption?: string;
  theme?: ThemeLike;
  fit?: 'cover' | 'contain';
  variant?: 'floating' | 'full' | 'polaroid';
  muted?: boolean;
  style?: React.CSSProperties;
};

function MediaFrame({
  src,
  caption,
  theme,
  fit = 'cover',
  variant = 'floating',
  muted = true,
  style,
}: MediaFrameProps) {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const resolved = themeOf(theme);
  const enter = spring({ frame, fps, config: { damping: 20, stiffness: 75 } });
  const opacity = fade(frame, 0, 20);
  const mediaStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: fit,
    display: 'block',
  };
  const isFull = variant === 'full';

  return (
    <div
      style={{
        position: isFull ? 'absolute' : 'relative',
        inset: isFull ? 0 : undefined,
        width: isFull ? '100%' : 'min(82%, 960px)',
        height: isFull ? '100%' : 'min(66%, 560px)',
        borderRadius: isFull ? 0 : variant === 'polaroid' ? 10 : 18,
        overflow: 'hidden',
        background: resolved.palette.surface,
        border: isFull ? 'none' : `1px solid ${resolved.palette.accent}55`,
        boxShadow: isFull ? 'none' : '0 28px 80px rgba(0, 0, 0, 0.38)',
        opacity,
        transform: isFull ? undefined : `translateY(${(1 - enter) * 28}px) scale(${0.97 + enter * 0.03})`,
        ...style,
      }}
    >
      {videoLike(src) ? (
        <OffthreadVideo src={src} pauseWhenBuffering muted={muted} style={mediaStyle} />
      ) : (
        <Img src={src} pauseWhenLoading style={mediaStyle} />
      )}
      {caption ? (
        <div
          style={{
            position: 'absolute',
            left: 24,
            right: 24,
            bottom: 22,
            padding: '13px 16px',
            borderRadius: 14,
            background: 'rgba(2, 6, 23, 0.72)',
            color: '#fff',
            fontSize: 24,
            lineHeight: 1.25,
            backdropFilter: 'blur(14px)',
          }}
        >
          {caption}
        </div>
      ) : null}
    </div>
  );
}

type StatItem = { label: string; value: string; detail?: string };

function StatGrid({
  items,
  theme,
  columns = 3,
}: {
  items: StatItem[];
  theme?: ThemeLike;
  columns?: 2 | 3 | 4;
}) {
  const frame = useCurrentFrame();
  const resolved = themeOf(theme);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gap: 18,
        width: 'min(86%, 1080px)',
      }}
    >
      {items.map((item, index) => {
        const opacity = fade(frame, 8 + index * 7, 18);
        const y = interpolate(frame, [8 + index * 7, 28 + index * 7], [28, 0], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });
        return (
          <div
            key={`${item.label}-${index}`}
            style={{
              minHeight: 150,
              padding: 24,
              borderRadius: 18,
              background: resolved.palette.surface,
              border: `1px solid ${resolved.palette.accent}44`,
              opacity,
              transform: `translateY(${y}px)`,
            }}
          >
            <div style={{ color: resolved.palette.accent2, fontSize: 20, fontWeight: 800 }}>{item.label}</div>
            <div style={{ color: resolved.palette.text, fontSize: 48, fontWeight: 900, marginTop: 12 }}>{item.value}</div>
            {item.detail ? (
              <div style={{ color: resolved.palette.muted, fontSize: 21, marginTop: 8, lineHeight: 1.25 }}>
                {item.detail}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function ProcessSteps({
  steps,
  theme,
}: {
  steps: Array<{ title: string; detail?: string }>;
  theme?: ThemeLike;
}) {
  const frame = useCurrentFrame();
  const resolved = themeOf(theme);
  return (
    <div style={{ width: 'min(84%, 1020px)', display: 'grid', gap: 18 }}>
      {steps.map((step, index) => {
        const opacity = fade(frame, 8 + index * 9, 18);
        return (
          <div
            key={`${step.title}-${index}`}
            style={{
              display: 'grid',
              gridTemplateColumns: '74px 1fr',
              gap: 22,
              alignItems: 'center',
              padding: '20px 24px',
              borderRadius: 18,
              background: resolved.palette.surface,
              border: `1px solid ${resolved.palette.accent}3f`,
              opacity,
            }}
          >
            <div
              style={{
                width: 58,
                height: 58,
                borderRadius: 18,
                display: 'grid',
                placeItems: 'center',
                background: resolved.palette.accent,
                color: '#fff',
                fontSize: 28,
                fontWeight: 900,
              }}
            >
              {index + 1}
            </div>
            <div>
              <div style={{ color: resolved.palette.text, fontSize: 31, fontWeight: 850 }}>{step.title}</div>
              {step.detail ? (
                <div style={{ color: resolved.palette.muted, fontSize: 22, marginTop: 6, lineHeight: 1.3 }}>
                  {step.detail}
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Comparison({
  leftTitle,
  rightTitle,
  rows,
  theme,
}: {
  leftTitle: string;
  rightTitle: string;
  rows: Array<{ left: string; right: string; label?: string }>;
  theme?: ThemeLike;
}) {
  const resolved = themeOf(theme);
  return (
    <div style={{ width: 'min(86%, 1120px)', display: 'grid', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
        {[leftTitle, rightTitle].map((title) => (
          <div
            key={title}
            style={{
              padding: '18px 22px',
              borderRadius: 16,
              background: resolved.palette.accent,
              color: '#fff',
              fontSize: 28,
              fontWeight: 900,
            }}
          >
            {title}
          </div>
        ))}
      </div>
      {rows.map((row, index) => (
        <div key={index} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
          {[row.left, row.right].map((value, col) => (
            <div
              key={`${index}-${col}`}
              style={{
                minHeight: 92,
                padding: '18px 22px',
                borderRadius: 16,
                background: resolved.palette.surface,
                border: `1px solid ${resolved.palette.accent}33`,
                color: resolved.palette.text,
                fontSize: 24,
                lineHeight: 1.28,
              }}
            >
              {row.label && col === 0 ? (
                <div style={{ color: resolved.palette.accent2, fontSize: 17, fontWeight: 800, marginBottom: 6 }}>
                  {row.label}
                </div>
              ) : null}
              {value}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function QuoteCard({
  quote,
  attribution,
  theme,
}: {
  quote: string;
  attribution?: string;
  theme?: ThemeLike;
}) {
  const frame = useCurrentFrame();
  const resolved = themeOf(theme);
  const opacity = fade(frame, 0, 24);
  return (
    <div
      style={{
        width: 'min(82%, 980px)',
        padding: '48px 56px',
        borderRadius: 22,
        background: resolved.palette.surface,
        border: `1px solid ${resolved.palette.accent}55`,
        boxShadow: '0 30px 80px rgba(0,0,0,0.32)',
        opacity,
      }}
    >
      <div style={{ color: resolved.palette.accent2, fontSize: 54, lineHeight: 1 }}>"</div>
      <div style={{ color: resolved.palette.text, fontSize: 42, lineHeight: 1.14, fontFamily: resolved.typography.display }}>
        {quote}
      </div>
      {attribution ? (
        <div style={{ color: resolved.palette.muted, fontSize: 24, marginTop: 26 }}>- {attribution}</div>
      ) : null}
    </div>
  );
}

function Timeline({
  items,
  theme,
}: {
  items: Array<{ time: string; title: string; detail?: string }>;
  theme?: ThemeLike;
}) {
  const frame = useCurrentFrame();
  const resolved = themeOf(theme);
  return (
    <div style={{ width: 'min(88%, 1120px)', display: 'grid', gridTemplateColumns: `repeat(${items.length}, 1fr)`, gap: 0 }}>
      {items.map((item, index) => {
        const opacity = fade(frame, 8 + index * 8, 18);
        return (
          <div key={`${item.time}-${index}`} style={{ position: 'relative', paddingTop: 44, opacity }}>
            <div
              style={{
                position: 'absolute',
                top: 20,
                left: 0,
                right: index === items.length - 1 ? '50%' : 0,
                height: 3,
                background: resolved.palette.accent,
              }}
            />
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 999,
                background: resolved.palette.accent2,
                border: '5px solid rgba(255,255,255,0.9)',
                marginBottom: 20,
              }}
            />
            <div style={{ color: resolved.palette.accent2, fontSize: 22, fontWeight: 900 }}>{item.time}</div>
            <div style={{ color: resolved.palette.text, fontSize: 28, fontWeight: 850, marginTop: 8 }}>{item.title}</div>
            {item.detail ? (
              <div style={{ color: resolved.palette.muted, fontSize: 20, lineHeight: 1.28, marginTop: 8, paddingRight: 18 }}>
                {item.detail}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function SectionDivider({
  label,
  title,
  subtitle,
  theme,
  variant = 'studio',
}: {
  label?: string;
  title: string;
  subtitle?: string;
  theme?: ThemeLike;
  variant?: SceneShellProps['variant'];
}) {
  return (
    <SceneShell theme={theme} variant={variant}>
      <AbsoluteFill style={{ justifyContent: 'center', alignItems: 'center', padding: 90 }}>
        <KineticTitle title={title} subtitle={subtitle} kicker={label} theme={theme} />
      </AbsoluteFill>
    </SceneShell>
  );
}

function LowerThird({
  title,
  subtitle,
  theme,
  align = 'left',
}: {
  title: string;
  subtitle?: string;
  theme?: ThemeLike;
  align?: 'left' | 'right';
}) {
  const frame = useCurrentFrame();
  const resolved = themeOf(theme);
  const opacity = fade(frame, 0, 18);
  const x = interpolate(frame, [0, 24], [align === 'left' ? -40 : 40, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  return (
    <div
      style={{
        position: 'absolute',
        left: align === 'left' ? 48 : undefined,
        right: align === 'right' ? 48 : undefined,
        bottom: 48,
        width: 'min(560px, 72%)',
        padding: '18px 22px',
        borderRadius: 18,
        background: resolved.palette.surface,
        border: `1px solid ${resolved.palette.accent}50`,
        boxShadow: '0 18px 48px rgba(0,0,0,0.28)',
        opacity,
        transform: `translateX(${x}px)`,
      }}
    >
      <div style={{ color: resolved.palette.text, fontSize: 28, fontWeight: 900, lineHeight: 1.08 }}>
        {title}
      </div>
      {subtitle ? (
        <div style={{ color: resolved.palette.muted, fontSize: 20, lineHeight: 1.24, marginTop: 6 }}>
          {subtitle}
        </div>
      ) : null}
    </div>
  );
}

function CaptionBar({
  text,
  theme,
  position = 'bottom',
}: {
  text: string;
  theme?: ThemeLike;
  position?: 'top' | 'bottom';
}) {
  const frame = useCurrentFrame();
  const resolved = themeOf(theme);
  const opacity = fade(frame, 0, 14);
  return (
    <div
      style={{
        position: 'absolute',
        left: '50%',
        [position]: 34,
        transform: 'translateX(-50%)',
        maxWidth: '82%',
        padding: '14px 20px',
        borderRadius: 16,
        background: 'rgba(2, 6, 23, 0.78)',
        border: `1px solid ${resolved.palette.accent}42`,
        color: '#fff',
        fontSize: 26,
        lineHeight: 1.28,
        textAlign: 'center',
        opacity,
        backdropFilter: 'blur(16px)',
      }}
    >
      {text}
    </div>
  );
}

function ProductShowcase({
  src,
  title,
  subtitle,
  points = [],
  theme,
}: {
  src: string;
  title: string;
  subtitle?: string;
  points?: string[];
  theme?: ThemeLike;
}) {
  const frame = useCurrentFrame();
  const resolved = themeOf(theme);
  const opacity = fade(frame, 0, 22);
  return (
    <SceneShell theme={theme} variant="studio">
      <AbsoluteFill
        style={{
          display: 'grid',
          gridTemplateColumns: '1.05fr 0.95fr',
          gap: 46,
          alignItems: 'center',
          padding: 82,
          opacity,
        }}
      >
        <MediaFrame src={src} theme={theme} fit="cover" variant="floating" style={{ width: '100%', height: 520 }} />
        <div>
          <KineticTitle title={title} subtitle={subtitle} theme={theme} align="left" maxWidth={620} />
          {points.length > 0 ? (
            <div style={{ display: 'grid', gap: 14, marginTop: 34 }}>
              {points.slice(0, 4).map((point, index) => (
                <div
                  key={`${point}-${index}`}
                  style={{
                    padding: '15px 18px',
                    borderRadius: 15,
                    background: resolved.palette.surface,
                    color: resolved.palette.text,
                    border: `1px solid ${resolved.palette.accent}38`,
                    fontSize: 22,
                    lineHeight: 1.25,
                  }}
                >
                  {point}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </AbsoluteFill>
    </SceneShell>
  );
}

function TransitionWash({
  theme,
  direction = 'left',
}: {
  theme?: ThemeLike;
  direction?: 'left' | 'right' | 'up' | 'down';
}) {
  const frame = useCurrentFrame();
  const resolved = themeOf(theme);
  const horizontal = direction === 'left' || direction === 'right';
  const distance = horizontal ? 1400 : 900;
  const start = direction === 'left' || direction === 'up' ? -distance : distance;
  const end = direction === 'left' || direction === 'up' ? distance : -distance;
  const travel = interpolate(frame, [0, 34], [start, end], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const opacity = interpolate(frame, [0, 12, 34], [0, 0.5, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill
      style={{
        pointerEvents: 'none',
        opacity,
        transform: horizontal ? `translateX(${travel}px)` : `translateY(${travel}px)`,
        background: horizontal
          ? `linear-gradient(90deg, transparent, ${resolved.palette.accent2}88, ${resolved.palette.accent}66, transparent)`
          : `linear-gradient(180deg, transparent, ${resolved.palette.accent2}88, ${resolved.palette.accent}66, transparent)`,
        filter: 'blur(22px)',
      }}
    />
  );
}

function Outro({
  title,
  subtitle,
  theme,
}: {
  title: string;
  subtitle?: string;
  theme?: ThemeLike;
}) {
  return (
    <SceneShell theme={theme} variant="studio">
      <AbsoluteFill style={{ justifyContent: 'center', alignItems: 'center', padding: 86 }}>
        <KineticTitle title={title} subtitle={subtitle} theme={theme} />
      </AbsoluteFill>
    </SceneShell>
  );
}

export const VideoKit = {
  SceneShell,
  KineticTitle,
  MediaFrame,
  StatGrid,
  ProcessSteps,
  Comparison,
  QuoteCard,
  Timeline,
  SectionDivider,
  LowerThird,
  CaptionBar,
  ProductShowcase,
  TransitionWash,
  Outro,
  utils: {
    clamp01,
    progress,
    fade,
    isVideo: videoLike,
    themeOf,
  },
};

export type VideoKitApi = typeof VideoKit;
